import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Divider } from '@components/divider';
import { DynamicInputFields } from '@components/dynamic-input-fields';
import { Field } from '@components/dynamic-input-fields/src/dynamic-input-fields.type';
import { HeaderEventFormSummary } from '@components/forms/multi-offer/header-summary';
import { BOOKING_FORM_FIELDS, TICKET_HOLDER_FORM_FIELDS } from '@domain/admission';
import classNames from 'classnames';

import { ModalFooter } from '../../modal-footer';
import { ModalV2 } from '../../modal-v2';

import { SummaryFormModalContentProps, SummaryFormModalProps } from './summary-form-modal.type';

export const SummaryFormModalContent = ({
  event,
  offer,
  addons,
  quantity,
  selectedAddons,
  optionalFields,
  showNominativeFields,
  title,
  mainActionText,
  mainActionIcon,
  isSubmitLoading,
  onContinue,
  handleClose,
}: SummaryFormModalContentProps) => {
  const { t } = useTranslation();
  const {
    formState: { isValid },
  } = useFormContext();

  const requiredFields = offer.bookingFormFields.filter((bookingFormField) => bookingFormField.isRequired) as Field[];
  const nominativeFields = offer.ticketHolderFormFields.filter(
    (ticketHolderFormField) => ticketHolderFormField.isRequired
  ) as Field[];
  const showTicketHolders = showNominativeFields && nominativeFields.length > 0;
  const Icon = mainActionIcon;

  return (
    <div className='relative max-h-[80vh] overflow-y-auto'>
      <HeaderEventFormSummary
        title={title}
        offer={offer}
        quantity={quantity}
        selectedAddons={selectedAddons}
        addons={addons}
        event={event}
        handleClose={handleClose}
      />
      <div className={classNames('flex flex-col gap-4 px-4 sm:px-20 pt-4 pb-6 sm:pb-12')}>
        {showTicketHolders && (
          <p className='text-primary text-subtitle-m md:text-subtitle-l'>{t('offers:ticketBuyerDetails')}</p>
        )}
        <DynamicInputFields
          parentFormField={BOOKING_FORM_FIELDS}
          fields={requiredFields}
          optionalFields={optionalFields}
          venueIsoCode={event.club.city.country.isoCode as string}
        />
        {showTicketHolders && (
          <>
            <Divider className='mt-0 mb-2 sm:mb-4' />
            <p className='text-primary text-subtitle-m md:text-subtitle-l'>{t('offers:ticketholderDetails')}</p>
            {Array.from({ length: quantity }, (_, index) => (
              <div key={`ticketholder-${index}`}>
                <p className='text-secondary text-subtitle-s md:text-subtitle-s'>
                  {t('offers:ticketholder')} {quantity > 1 ? `#${index + 1}` : ''}
                </p>
                <DynamicInputFields
                  parentFormField={`${TICKET_HOLDER_FORM_FIELDS}.${index}`}
                  fields={nominativeFields}
                  optionalFields={false}
                  venueIsoCode={event.club.city.country.isoCode as string}
                />
                {index + 1 < quantity && <Divider className='mt-0 mb-2 sm:mb-4' />}
              </div>
            ))}
          </>
        )}
      </div>
      <ModalFooter
        primaryButtonText={
          <>
            {Icon && <Icon className='w-4 h-4 mr-2' />}{' '}
            <span className='flex items-center gap-2'>{mainActionText}</span>
          </>
        }
        onPrimaryClick={onContinue}
        secondaryButtonText={t('common:discard')}
        onSecondaryClick={handleClose}
        isPrimaryDisabled={!isValid}
        isPrimaryLoading={isSubmitLoading}
      />
    </div>
  );
};

export const SummaryFormModal = ({ open, ...summaryFormContentProps }: SummaryFormModalProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(open);
  const handleClose = () => setIsOpen(false);
  return (
    <ModalV2 isOpen={isOpen} onClose={handleClose}>
      <SummaryFormModalContent {...summaryFormContentProps} />
    </ModalV2>
  );
};
